/* ==========================================================================
   vv-delivery-estimate.js — port of the GemPages delivery-estimate logic
   --------------------------------------------------------------------------
   • Cutoff 15:00 Europe/Bucharest
   • Romanian fixed holidays + Easter-based dates 2026–2028 (exact GP lists)
   • Ship +1..4 business days (MESTERINO/KLUGMEISTER → +5..7, read from
     data-mesterino="1" written by Liquid)
   • Year-agnostic Christmas/New-Year warning window (Nov 24 → Jan 10)
   • Renders into [data-vv-delivery]; hides when the variant is unavailable
   • Re-renders on the custom 'vv:variant-change' event and every minute
     (countdown refresh)
   ========================================================================== */
(() => {
  'use strict';

  // ── Config ────────────────────────────────────────────────────────────
  const CUTOFF        = 15;
  const DELIVERY_MIN  = 1;
  const DELIVERY_MAX  = 4;
  const MESTERINO_MIN = 5;
  const MESTERINO_MAX = 7;
  const TZ            = 'Europe/Bucharest';

  // ── Holidays (exact lists from the GP snippet) ────────────────────────
  const HOL_FIXED = new Set([
    '01-01', '01-02', '01-06', '01-07',
    '05-01', '06-01', '08-15', '11-30', '12-01', '12-25', '12-26'
  ]);
  const HOL_DATED = new Set([
    '2026-04-10', '2026-04-12', '2026-04-13', '2026-05-31', '2026-06-01',
    '2027-04-30', '2027-05-02', '2027-05-03', '2027-06-20', '2027-06-21',
    '2028-04-14', '2028-04-16', '2028-04-17', '2028-06-04', '2028-06-05'
  ]);

  const MONTHS = ['Ian', 'Feb', 'Martie', 'Apr', 'Mai', 'Iunie', 'Iulie', 'Aug', 'Sept', 'Oct', 'Noi', 'Dec'];

  // ── Helpers ───────────────────────────────────────────────────────────
  const pad = (n) => String(n).padStart(2, '0');
  const md  = (d) => pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  const ymd = (d) => d.getFullYear() + '-' + md(d);

  const isHoliday = (d) => HOL_FIXED.has(md(d)) || HOL_DATED.has(ymd(d));
  const isBiz     = (d) => { const w = d.getDay(); return w !== 0 && w !== 6 && !isHoliday(d); };
  const addBiz    = (date, n) => {
    const r = new Date(date);
    let c = 0;
    while (c < n) { r.setDate(r.getDate() + 1); if (isBiz(r)) c++; }
    return r;
  };
  const fmtDate = (d) => d.getDate() + ' ' + MONTHS[d.getMonth()];
  const nowTZ   = () => new Date(new Date().toLocaleString('en-US', { timeZone: TZ }));

  const timeUntilCutoff = () => {
    const n = nowTZ();
    const c = new Date(n);
    c.setHours(CUTOFF, 0, 0, 0);
    if (n.getHours() >= CUTOFF) c.setDate(c.getDate() + 1);
    const diff = c - n;
    return { h: Math.floor(diff / 3600000), m: Math.floor((diff % 3600000) / 60000) };
  };

  // ── Holiday warning (year-agnostic: Nov 24 → Jan 10 window) ──────────
  const holidayWarning = (deliveryEnd) => {
    const now = nowTZ();
    now.setHours(0, 0, 0, 0);
    const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();

    let xy; // Christmas year
    if (m === 11 || (m === 10 && d >= 24)) xy = y;
    else if (m === 0 && d <= 10)           xy = y - 1;
    else return '';

    const xmas = new Date(xy, 11, 25);     xmas.setHours(0, 0, 0, 0);
    const ny   = new Date(xy + 1, 0, 1);   ny.setHours(0, 0, 0, 0);
    const end  = new Date(deliveryEnd);    end.setHours(0, 0, 0, 0);

    const pastXmas = y > xy || (m === 11 && d >= 25);
    const isDec24  = y === xy && m === 11 && d === 24;

    let text = '';
    if (pastXmas) {
      if (end > ny) text = 'Posibil să primești comanda după 12.01.' + (xy + 1);
    } else if (isDec24) {
      if (end > xmas) text = 'Vei primi comanda după Crăciun';
    } else {
      if (end > xmas) text = 'Posibil să primești comanda după Crăciun';
    }

    return text ? '<p class="vv-holiday-warning">⚠️ ' + text + '</p>' : '';
  };

  // ── Message builder ───────────────────────────────────────────────────
  const buildMessage = (mesterino) => {
    const dmin = mesterino ? MESTERINO_MIN : DELIVERY_MIN;
    const dmax = mesterino ? MESTERINO_MAX : DELIVERY_MAX;

    const now = nowTZ();
    let ship = new Date(now);
    if (now.getHours() >= CUTOFF || !isBiz(now)) ship = addBiz(ship, 1);

    const s = addBiz(ship, dmin);
    const e = addBiz(ship, dmax);
    const t = timeUntilCutoff();

    const ICON_CHECK = '<svg class="vv-delivery__icon" aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m8 12.5 2.5 2.5L16 9.5"/></svg>';
    const ICON_TRUCK = '<svg class="vv-delivery__icon" aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>';

    return '<div class="vv-delivery__row">'
      + ICON_CHECK
      + '<span><span class="vv-instock">În Stoc</span><span class="vv-delivery__sep">·</span>'
      + 'Comandă în următoarele <span class="vv-bold">' + t.h + 'h ' + t.m + 'm</span></span>'
      + '</div>'
      + '<div class="vv-delivery__row">'
      + ICON_TRUCK
      + '<span>Primești comanda între <span class="vv-bold">' + fmtDate(s) + ' – ' + fmtDate(e) + '</span></span>'
      + '</div>'
      + holidayWarning(e);
  };

  // ── State + render ────────────────────────────────────────────────────
  const state = { available: true };

  const readInitialAvailability = () => {
    const script = document.querySelector('script[data-vv-product]');
    if (!script) return;
    try {
      const product = JSON.parse(script.textContent);
      const variants = product.variants || [];
      const urlId = new URLSearchParams(window.location.search).get('variant');
      const variant =
        (urlId && variants.find((v) => String(v.id) === String(urlId))) ||
        variants.find((v) => v.available) ||
        variants[0];
      state.available = !!(variant && variant.available);
    } catch (e) { /* keep default */ }
  };

  const render = () => {
    const el = document.querySelector('[data-vv-delivery]');
    if (!el) return;
    if (!state.available) {
      el.innerHTML = '';
      el.style.display = 'none';
      return;
    }
    el.style.display = '';
    el.innerHTML = buildMessage(el.dataset.mesterino === '1');
  };

  const start = () => {
    if (!document.querySelector('[data-vv-delivery]')) return;
    readInitialAvailability();
    render();

    document.addEventListener('vv:variant-change', (e) => {
      const variant = e.detail && e.detail.variant;
      if (variant) state.available = !!variant.available;
      render();
    });

    // Refresh the countdown once a minute.
    setInterval(render, 60000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
